import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { accumulate } from '@olivierpascal/helpers';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IAppRegion } from '~/lib/appSettings';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useFormAutoFocus } from '~/hooks/useFormAutoFocus';
import { ApiError } from '~/lib/api';
import { useChangePasswordMutation } from '~/lib/api/modules/Auth/hooks/useChangePasswordMutation';
import { useRequestPasswordChangeMutation } from '~/lib/api/modules/Auth/hooks/useRequestPasswordChangeMutation';
import { getAppSettings } from '~/lib/appSettings';
import { storageFactory } from '~/lib/storage';
import { ResetPasswordFormMainSection } from './ResetPasswordFormMainSection';

export type IResetPasswordFormValues = {
  appRegion?: IAppRegion;
  email?: string;
  token?: string;
  password?: string;
  passwordConfirmation?: string;
};

export type IResetPasswordFormProps = IContainerProps & {
  mode: 'request-change' | 'change';
  onSuccess?: () => void;
  cancelUrl?: string;
  defaultValues?: DefaultValues<IResetPasswordFormValues>;
};

export const ResetPasswordForm: React.FC<IResetPasswordFormProps> = (props) => {
  const { sx, mode, onSuccess, cancelUrl, defaultValues } = props;
  const { t } = useTranslation();
  const requestPasswordChangeMutation = useRequestPasswordChangeMutation();
  const changePassword = useChangePasswordMutation();
  const appSettings = getAppSettings();
  const defaultAppRegion = appSettings.defaultRegion;
  const storageRef = useRef(storageFactory('local')());
  const form = useForm<IResetPasswordFormValues>({
    defaultValues: accumulate(
      {
        appRegion:
          storageRef.current.get('preferredAppRegion') ?? defaultAppRegion,
      },
      defaultValues,
    ),
  });
  useFormAutoFocus({ form, name: 'email' });

  const appRegion = form.watch('appRegion');
  useEffect(() => {
    if (appRegion) {
      void storageRef.current.set('preferredAppRegion', appRegion);
    }
  }, [appRegion]);

  const handleSubmit: SubmitHandler<IResetPasswordFormValues> = (values) => {
    if (requestPasswordChangeMutation.isLoading) {
      return;
    }

    if (mode === 'change') {
      return changePassword
        .mutateAsync(
          {
            region: values.appRegion ?? defaultAppRegion,
            body: {
              appId: 'crowdaa_app_id',
              email: values.email ?? '',
              password: values.password ?? '',
              token: values.token ?? '',
            },
          },
          { onSuccess },
        )
        .catch((error) => {
          if (error instanceof ApiError) {
            if (error.errors.some((error) => error.code === 'token_expired')) {
              throw new Error(t('ResetPasswordForm.token-expired'));
            }

            throw error;
          }
        });
    }

    return requestPasswordChangeMutation
      .mutateAsync(
        {
          region: values.appRegion ?? defaultAppRegion,
          body: {
            email: values.email ?? '',
          },
        },
        { onSuccess },
      )
      .catch((error) => {
        if (error instanceof ApiError) {
          if (error.errors.some((error) => error.code === 'email_not_found')) {
            throw new Error(t('ResetPasswordForm.email-address-not-found'));
          } else if (
            error.errors.some((error) => error.code === 'token_already_sent')
          ) {
            throw new Error(t('ResetPasswordForm.token-already-sent'));
          }

          throw error;
        }
      });
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.FormBody
        form={form}
        onSubmit={handleSubmit}
        cancelUrl={cancelUrl}
        submitButtonLabel={t('ResetPasswordForm.reset-password')}
      >
        <ResetPasswordFormMainSection mode={mode} form={form} />
      </ContentBlock.FormBody>
    </ContentBlock>
  );
};
